import React, { useMemo } from 'react'
import { map, uniq, sortBy, toString, startCase } from 'lodash'

import Filters from 'Features/Filters'
import { arrayToOptions } from 'Utilities/selectOptions'

const getFromVMs = (vms, field) => sortBy(uniq(map(vms, field)))
const capitalizeSplit = (string) => {
  if (string === 'N/A') { return string }

  return toString(string).split(/(?=[A-Z])/).join(' ')
}

const defaultFilters = {
  locations: 'All',
  clusters: 'All',
  dataProtectionCompliance: 'All',
  dataProtectionStatus: 'All',
  monitoringCompliance: 'All',
  antivirusCompliance: 'All',
  antivirusStatus: 'All',
  patchingCompliance: 'All',
  patchingStatus: 'All',
  calligoManagement: 'All',
  deviceTypes: 'All',
  powerState: 'All',
  customerStatus: 'All',
  osSupported: 'All',
  reservationCost: 'All'
}

const VMIntegrationReportFilters = ({ filters = {}, changeFilters, vms }) => {
  const booleanOptions = useMemo(() => [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' }
  ])
  const monitoringCompliances = useMemo(() => arrayToOptions(getFromVMs(vms, 'monitoring_compliance'), capitalizeSplit))
  const antivirusCompliances = useMemo(() => (
    [
      ...arrayToOptions(getFromVMs(vms, 'antivirus_compliance'), capitalizeSplit),
      { value: 'wrongAntivirusProduct', label: 'Wrong Antivirus Product'}
    ]
  ))
  const antivirusStatuses = useMemo(() => arrayToOptions(getFromVMs(vms, 'antivirus_status'), capitalizeSplit))
  const patchingStatuses = useMemo(() => arrayToOptions(getFromVMs(vms, 'patching_status'), capitalizeSplit))
  const patchingCompliances = useMemo(() => arrayToOptions(getFromVMs(vms, 'patching_compliance'), capitalizeSplit))
  const dataProtectionCompliances = useMemo(() => arrayToOptions(getFromVMs(vms, 'data_protection_compliance'), capitalizeSplit))
  const dataProtectionStatuses = useMemo(() => arrayToOptions(getFromVMs(vms, 'data_protection_status'), capitalizeSplit))
  const osSupportedOptions = useMemo(() => arrayToOptions(getFromVMs(vms, 'os_supported')))
  const clusters = useMemo(() => arrayToOptions(getFromVMs(vms, 'cluster')))
  const locations = useMemo(() => arrayToOptions(getFromVMs(vms, 'location')))
  const deviceTypes = useMemo(() => arrayToOptions(getFromVMs(vms, 'device_type')))
  const powerState = useMemo(() => arrayToOptions(['All', ...getFromVMs(vms, 'power_state')]))
  const customerStatuses = useMemo(() => arrayToOptions(['live', 'historic', 'pending offboarding'], startCase))
  const calligoManagement = useMemo(() => [
    { value: "true", label: 'Calligo Management VM' },
    { value: "false", label: 'Customer' }
  ])

  const integrationStatuses = useMemo(() => [
    { value: "true", label: 'Integrated' },
    { value: "false", label: 'Not integrated' },
    { value: "n/a", label: 'N/A' },
  ])

  return (
    <Filters {...{ filters, onChange: changeFilters, shareable: true }}>
      <div className="col">
        <div className='row'>
          <Filters.Select {...{ filterName: 'locations', label: 'Locations', options: locations }} />
          <Filters.Select {...{ filterName: 'clusters', label: 'Clusters', options: clusters }} />
          <Filters.Select {...{ filterName: 'calligoManagement', options: calligoManagement }} />
          <Filters.Select {...{ filterName: 'deviceTypes', label: 'Device Type', options: deviceTypes }} />
        </div>

        <div className='row mb-3'>
          <Filters.Select {...{ filterName: 'reservationCost', label: 'Reservation cost', options: booleanOptions }} />
          <Filters.Select {...{ filterName: 'powerState', label: 'Power State', options: powerState, isMulti: false }} />
          <Filters.Select {...{ filterName: 'customerStatus', label: 'Customer status', options: customerStatuses }} />
          <Filters.Select {...{ filterName: 'osSupported', label: 'OS Supported', options: osSupportedOptions }} />
        </div>

        <div className='row'>
          <Filters.Select {...{ filterName: 'dataProtectionCompliance', label: 'Data Protection Compliance', options: dataProtectionCompliances, className: 'col-3' }} />
          <Filters.Select {...{ filterName: 'patchingCompliance', label: 'Patching Compliance', options: patchingCompliances, className: 'col-3' }} />
          <Filters.Select {...{ filterName: 'antivirusCompliance', label: 'AntiVirus Compliance', options: antivirusCompliances, className: 'col-3' }} />
          <Filters.Select {...{ filterName: 'monitoringCompliance', label: 'Monitoring Compliance', options: monitoringCompliances }} />
        </div>

        <div className='row'>
          <Filters.Select {...{ filterName: 'dataProtectionStatus', label: 'Data Protection Status', options: dataProtectionStatuses, className: 'col-3' }} />
          <Filters.Select {...{ filterName: 'patchingStatus', label: 'Patching Status', options: patchingStatuses, className: 'col-3' }} />
          <Filters.Select {...{ filterName: 'antivirusStatus', label: 'AntiVirus Status', options: antivirusStatuses, className: 'col-3' }} />
        </div>
      </div>
    </Filters>
  )
}

export { VMIntegrationReportFilters as Filters, defaultFilters }
